import { Fragment, useContext, useEffect, useState } from "react";
import { Alert, Button, Container, Row, Stack } from "react-bootstrap";
import { AuthContext } from "../../Auth/AuthProvider";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { AbpnSpinner } from '../../Shared/Components/AbpnSpinner';
import HeaderWithTitle from '../../Shared/Components/HeaderWithTitle';
import { abpnClient } from "../../Services/AbpnClient";
import PersonalInformationForm from "./PersonalInformationForm";
import MedicalEducationCard from "./MedicalEducationCard";
import { MedicalEducation } from "../Models/MedicalEducation";
import Resident, { ResidentDetail } from "../Models/Resident";
import { MailToModel } from "../../Shared/Models/MailToModel";
import ResidentInformation from "../Models/ResidentInformation";
import { useNavigate, useParams } from "react-router-dom";
import { ProfileContext } from "../../Auth/ProfileProvider";

export function ResidentInformationForm() {
    const authContext = useContext(AuthContext);
    const { physicianId } = useParams();
    const navigate = useNavigate();

    const { user } = useContext(ProfileContext);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
    const [isNewResidentAlert, setIsNewResidentAlert] = useState<boolean>(false);
    const [showDuplicateAlert, setShowDuplicateAlert] = useState<boolean>(false);
    const [runValidation, setRunValidation] = useState<boolean>(false);
    const [resetForms, setResetForms] = useState<boolean>(false);
    const [isMedicalEducationFormValid, setIsMedicalEducationFormValid] = useState<boolean>(null);
    const [isPersonalInfoFormValid, setIsPersonalInfoFormValid] = useState<boolean>(null);
    const [residentInfo, setResidentInfo] = useState<ResidentInformation>(null);
    const [closeForm, setCloseForm] = useState<boolean>(true);
    const [mailToLink, setMailToLink] = useState<string>(null);
    const [saveForm, setSaveForm] = useState<boolean>(false);
    const [originalResident, setOriginalResident] = useState<ResidentInformation>(null);
    let userProgramTypeId = user.trainingPrograms.find(
        (_) => _.trainingProgramId === user.selectedTrainingProgramId
      ).trainingProgramTypeId;
    let url = `/api/dashboard/GetCurrentResidents?programTypeId=${userProgramTypeId}`;

    useEffect(() => {
        const createNewPhysician = () => {
            let resident = new Resident();
            let medicalEducation = new MedicalEducation();
            setResidentInfo(new ResidentInformation(resident, medicalEducation));
        }

        const getResident = async () => {
            setIsLoading(true);
            const residents = await abpnClient.get<Array<Resident>>(url, authContext);
            const resident = residents.find((x) => x.physicianId === parseInt(physicianId));
            const medicalEducation = await abpnClient.get<MedicalEducation>(`/api/training/GetMedicalEducation?physicianId=${physicianId}`, authContext);
            setResidentInfo(new ResidentInformation(resident, medicalEducation));
            setOriginalResident(new ResidentInformation(resident, medicalEducation));
            setIsLoading(false);
          };

        const getMailToLink = () => {
            abpnClient.get<MailToModel>(`/api/profile/GetMailToAddress`, authContext)
                .then(result => {
                    setMailToLink(result.emailAddress);
                });
        }
        getMailToLink();
        if (physicianId) {
            getResident();
        } else {
            createNewPhysician();
        }
    }, []);

    const validateAndSave = (closeForm: boolean) => {
        setCloseForm(closeForm);
        setRunValidation(true);
        setSaveForm(true);
    }

    useEffect(() => {
        const SaveResident = async () => {
            
            setRunValidation(false);

            if (residentInfo.resident?.isNewResident) setIsNewResidentAlert(true);
            const url = `/api/training/SavePhysicianInformation?programTypeId=${userProgramTypeId}`;
            let residentInformation = new ResidentDetail(residentInfo.resident, residentInfo.medicalEducation);
            let updatedResidentInformation = await abpnClient.post<ResidentInformation>(url, residentInformation, authContext);
            updatedResidentInformation.resident.showDetails = true;
            setResetForms(true);
            setIsSaving(false);
            setShowSuccessAlert(true);
            setIsMedicalEducationFormValid(null);
            setIsPersonalInfoFormValid(null);
            setTimeout(() => {
                setShowSuccessAlert(false);
                let newResidentInfo = new ResidentInformation(new Resident(), new MedicalEducation());
                setResidentInfo(newResidentInfo);
                setOriginalResident(newResidentInfo);
                if (closeForm) {
                    if (physicianId) {
                        navigate(`/${physicianId}`);
                    } else {
                        navigate("/");
                    }
                }
            }, 2000);
        }

        const validateAndSaveTraining = async () => {
            setIsSaving(true);

            if (shouldValidateForDuplicates()) {
                let isValid = await abpnClient.post<boolean>("/api/training/ValidateNoDuplicatePhysicianSave", residentInfo.resident, authContext);

                if (!isValid) {
                    setShowDuplicateAlert(true);
                    setIsSaving(false);
                    setSaveForm(false);
                    return;
                }
            }
            SaveResident();
        }

        if (isMedicalEducationFormValid && isPersonalInfoFormValid && saveForm) {
            validateAndSaveTraining();
        }
    }, [isMedicalEducationFormValid, isPersonalInfoFormValid, saveForm]);

    const shouldValidateForDuplicates = () => {
        if (residentInfo.resident.isNewResident) return true;

        return (originalResident.resident.dateOfBirth !== residentInfo.resident.dateOfBirth || originalResident.resident.ssn4 !== residentInfo.resident.ssn4);
    }

    const handleUpdate = () => {
        setIsMedicalEducationFormValid(null);
        setIsPersonalInfoFormValid(null);
        setRunValidation(false);
        setResetForms(false);
    }
    const updateMedicalEducation = (medicalEducation: MedicalEducation) => {
        handleUpdate();
        let updated = deepCopy(residentInfo);
        updated.medicalEducation = medicalEducation;
        setResidentInfo(updated);
    }

    const updateResidentName = (resident: Resident) => {
        handleUpdate();
        let updated = deepCopy(residentInfo);
        updated.resident = resident;
        setResidentInfo(updated);
    }

    const updateResidentDegreeType = (degreeType: string) => {
        handleUpdate();
        let updated = deepCopy(residentInfo);
        updated.resident.name.title = degreeType;
        setResidentInfo(updated);
    }

    const cancelEdit = () => {
        setResidentInfo(null);
        setSaveForm(false);
        if (physicianId) {
            navigate(`/${physicianId}`);
        } else {
            navigate("/");
        }
    }

    let headerTitle = residentInfo?.resident?.isNewResident  ? "Add" : "Edit";
    let successAlertBody = isNewResidentAlert ? "New resident successfully added." : "Resident successfully updated."
    let duplicateValidationBody = isNewResidentAlert ? "The resident you are trying to create already exists in preCERT. For transfers, please use the transfer option to search for the resident you need to transfer into your program.If you have trouble searching for the resident or have any questions, please email"
                                                          : "The resident information you are trying to save already exists on another resident in preCERT. If the you entered the information incorrectly, please try again. If the information is correct and you need help, please email";

    if (isLoading) return <AbpnSpinner />;

    return (
        <Container>
            <Alert className={showSuccessAlert ? "visible" : "invisible"} variant={'success'}>{successAlertBody}</Alert>
            
            <HeaderWithTitle title={`${headerTitle} Resident`} subTitle={null} />

            {showDuplicateAlert && 
                <Alert variant={'danger'} onClose={() => setShowDuplicateAlert(false)} dismissible> 
                    {duplicateValidationBody} <a href={`mailto: ${mailToLink}`}>credentials@abpn.org</a>.
                </Alert>
            }
            
            <PersonalInformationForm
                newResident={residentInfo?.resident}
                runValidation={runValidation}
                resetForm={resetForms}
                isFormValid={isPersonalInfoFormValid}
                updatePersonalInformation={(resident: Resident) => updateResidentName(resident)}
                updatePersonalInformationValidation={(isPersonalInformationValid: boolean) => setIsPersonalInfoFormValid(isPersonalInformationValid)} />

            <MedicalEducationCard
                runValidation={runValidation}
                resetForm={resetForms}
                isFormValid={isMedicalEducationFormValid}
                medicalEducation={residentInfo?.medicalEducation ?? new MedicalEducation()}
                degreeType={residentInfo?.resident?.name?.title}
                updateMedicalEducation={(medicalEducation: MedicalEducation) => updateMedicalEducation(medicalEducation)}
                updateDegreeType={(degreeType: string) => updateResidentDegreeType(degreeType)}
                updateMedicalEducationValidation={(isMedicalEducationValid: boolean) => setIsMedicalEducationFormValid(isMedicalEducationValid)} />

            <Row className="align-items-end">
                <Stack direction={"horizontal"} gap={3} className={"mt-5"}>
                    {isSaving ? <AbpnSpinner /> :
                    <Fragment>
                        <div>
                            <Button variant="primary" disabled={showSuccessAlert} type="button" onClick={() => validateAndSave(true)}>Save</Button>
                        </div>
                        <div>
                            <Button variant="primary" disabled={showSuccessAlert} hidden={!residentInfo?.resident?.isNewResident} type="button" onClick={() => validateAndSave(false)}>Save And Create Another</Button>
                        </div>
                        <div>
                            <Button variant="primary" disabled={showSuccessAlert} type="button" onClick={() => cancelEdit()}>Cancel</Button>
                        </div>
                    </Fragment>}
                </Stack>
            </Row>
        </Container>
    );
}

export default ResidentInformationForm;